import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PressReleases = () => {
  const [activeTab, setActiveTab] = useState('releases');
  const [releases, setReleases] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [freeSites, setFreeSites] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showOutletModal, setShowOutletModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('adminToken');

  const [generateForm, setGenerateForm] = useState({
    title: '',
    topic: '',
    keywords: '',
    custom_content: '',
    generate_with_ai: true
  });

  const [outletForm, setOutletForm] = useState({
    name: '',
    email: '',
    type: 'newspaper',
    location: 'fort_worth',
    notes: ''
  });

  const [sendForm, setSendForm] = useState({
    selectedOutlets: [],
    include_free_sites: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [releasesRes, outletsRes, schedulerRes, sitesRes] = await Promise.all([
        fetch(`${API_URL}/api/press-releases`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/press-releases/outlets`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/press-releases/scheduler/status`),
        fetch(`${API_URL}/api/press-releases/free-sites`)
      ]);
      
      if (releasesRes.ok) setReleases((await releasesRes.json()).press_releases || []);
      if (outletsRes.ok) {
        const data = await outletsRes.json();
        setOutlets(data.outlets || []);
      }
      if (schedulerRes.ok) setSchedulerStatus(await schedulerRes.json());
      if (sitesRes.ok) setFreeSites((await sitesRes.json()).sites || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/press-releases/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...generateForm,
          keywords: generateForm.keywords.split(',').map(k => k.trim()).filter(k => k)
        })
      });
      if (res.ok) {
        await fetchData();
        setShowGenerateModal(false);
        setGenerateForm({ title: '', topic: '', keywords: '', custom_content: '', generate_with_ai: true });
      }
    } catch (err) { console.error('Failed to generate:', err); }
    finally { setGenerating(false); }
  };

  const handleAddOutlet = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/press-releases/outlets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(outletForm)
      });
      if (res.ok) {
        await fetchData();
        setShowOutletModal(false);
        setOutletForm({ name: '', email: '', type: 'newspaper', location: 'fort_worth', notes: '' });
      }
    } catch (err) { console.error('Failed to add outlet:', err); }
  };

  const handleSend = async () => {
    if (!selectedRelease) return;
    try {
      const res = await fetch(`${API_URL}/api/press-releases/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          press_release_id: selectedRelease.id,
          outlet_ids: sendForm.selectedOutlets,
          include_free_sites: sendForm.include_free_sites
        })
      });
      if (res.ok) {
        const result = await res.json();
        alert(`Press release sent to ${result.sent_count} outlets!`);
        await fetchData();
        setShowSendModal(false);
        setSelectedRelease(null);
      }
    } catch (err) { console.error('Failed to send:', err); }
  };

  const handlePublish = async (release) => {
    await fetch(`${API_URL}/api/press-releases/${release.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'published' })
    });
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this press release?')) return;
    await fetch(`${API_URL}/api/press-releases/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchData();
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/press-releases/scheduler/generate-now`, { method: 'POST' });
      if (res.ok) {
        alert('Press release generated!');
        fetchData();
      }
    } catch (err) { console.error(err); }
    setGenerating(false);
  };

  const toggleScheduler = async () => {
    const endpoint = schedulerStatus?.is_running ? 'stop' : 'start';
    await fetch(`${API_URL}/api/press-releases/scheduler/${endpoint}`, { method: 'POST' });
    fetchData();
  };

  const updateSchedulerConfig = async (key, value) => {
    await fetch(`${API_URL}/api/press-releases/scheduler/config?${key}=${value}`, { method: 'PUT' });
    fetchData();
  };

  const toggleOutletSelection = (outletId) => {
    setSendForm(prev => ({
      ...prev,
      selectedOutlets: prev.selectedOutlets.includes(outletId)
        ? prev.selectedOutlets.filter(id => id !== outletId)
        : [...prev.selectedOutlets, outletId]
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-700',
      published: 'bg-green-100 text-green-700',
      sent: 'bg-blue-100 text-blue-700',
      approved: 'bg-purple-100 text-purple-700'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.draft}`}>{status}</span>;
  };

  const getSiteTypeBadge = (type) => {
    const styles = {
      free: 'bg-green-100 text-green-700',
      freemium: 'bg-blue-100 text-blue-700',
      pitch: 'bg-purple-100 text-purple-700'
    };
    return <span className={`px-2 py-0.5 rounded text-xs ${styles[type] || 'bg-gray-100'}`}>{type}</span>;
  };

  const tabs = [
    { id: 'releases', label: 'Press Releases', icon: '📰' },
    { id: 'scheduler', label: 'Auto-Scheduler', icon: '🤖' },
    { id: 'outlets', label: 'Media Outlets', icon: '📡' },
    { id: 'free-sites', label: 'Free PR Sites', icon: '🆓' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Press Releases | Admin</title></Helmet>

      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">📰 Press Release Management</h1>
            <p className="text-blue-100 text-sm">Generate, manage, and distribute press releases</p>
          </div>
          <button onClick={() => setShowGenerateModal(true)} className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100">
            ✨ Generate New PR
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 px-6 bg-white">
        <nav className="flex space-x-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {/* Press Releases Tab */}
        {activeTab === 'releases' && (
          <div>
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Total PRs</p>
                <p className="text-2xl font-bold">{releases.length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Published</p>
                <p className="text-2xl font-bold text-green-600">{releases.filter(r => r.status === 'published').length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                <p className="text-gray-500 text-sm">Drafts</p>
                <p className="text-2xl font-bold text-yellow-600">{releases.filter(r => r.status === 'draft').length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Sent</p>
                <p className="text-2xl font-bold text-purple-600">{releases.filter(r => r.status === 'sent').length}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="divide-y">
                {releases.map(release => (
                  <div key={release.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800">{release.title}</h3>
                          {getStatusBadge(release.status)}
                          {release.auto_generated && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">Auto</span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{release.topic}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Created: {new Date(release.created_at).toLocaleDateString()} | Views: {release.views || 0}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {release.status === 'draft' && (
                          <button onClick={() => handlePublish(release)} className="text-green-600 hover:text-green-800 text-sm">Publish</button>
                        )}
                        {release.status === 'published' && (
                          <button onClick={() => { setSelectedRelease(release); setShowSendModal(true); }} className="text-blue-600 hover:text-blue-800 text-sm">Send</button>
                        )}
                        <button onClick={() => handleDelete(release.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                      </div>
                    </div>
                    {release.excerpt && <p className="text-sm text-gray-600 mt-2 italic">"{release.excerpt.substring(0, 150)}..."</p>}
                  </div>
                ))}
                {releases.length === 0 && <div className="p-8 text-center text-gray-500">No press releases yet. Generate your first one!</div>}
              </div>
            </div>
          </div>
        )}

        {/* Scheduler Tab */}
        {activeTab === 'scheduler' && schedulerStatus && (
          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">🤖 Auto-Generation Scheduler</h3>
                  <p className="text-gray-600 text-sm">Automatically generate 1-2 press releases per month on rotating topics</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${schedulerStatus.is_running ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {schedulerStatus.is_running ? '● Running' : '○ Stopped'}
                  </span>
                  <button onClick={toggleScheduler} className={`px-4 py-2 rounded font-medium ${schedulerStatus.is_running ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                    {schedulerStatus.is_running ? 'Stop' : 'Start'}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-800 mb-4">⚙️ Configuration</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Releases per Month</label>
                    <select value={schedulerStatus.config?.releases_per_month || 2} onChange={e => updateSchedulerConfig('releases_per_month', e.target.value)} className="border rounded px-3 py-2 w-full">
                      <option value={1}>1 per month</option>
                      <option value={2}>2 per month</option>
                      <option value={3}>3 per month</option>
                      <option value={4}>4 per month</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={schedulerStatus.config?.enabled} onChange={e => updateSchedulerConfig('enabled', e.target.checked)} className="rounded" />
                    <span className="text-sm text-gray-700">Enable auto-generation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={schedulerStatus.config?.auto_send} onChange={e => updateSchedulerConfig('auto_send', e.target.checked)} className="rounded" />
                    <span className="text-sm text-gray-700">Auto-send to outlets (not recommended)</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-800 mb-4">📅 Schedule Status</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Generated:</span>
                    <span className="font-medium">{schedulerStatus.last_run?.run_at ? new Date(schedulerStatus.last_run.run_at).toLocaleDateString() : 'Never'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days Until Next:</span>
                    <span className="font-medium">{schedulerStatus.days_until_next_release ?? 'N/A'} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Next Topic:</span>
                    <span className="font-medium text-blue-600">{schedulerStatus.next_topic?.topic || 'N/A'}</span>
                  </div>
                  <button onClick={handleGenerateNow} disabled={generating} className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                    {generating ? '🔄 Generating...' : '⚡ Generate Now'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h4 className="font-semibold text-gray-800 mb-4">📋 Topic Rotation Queue</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                {schedulerStatus.config?.topics_rotation?.map((t, i) => (
                  <div key={i} className={`p-3 rounded border ${i === (schedulerStatus.config?.current_topic_index % schedulerStatus.config?.topics_rotation.length) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    <p className="font-medium text-gray-800">{t.topic}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.keywords?.slice(0, 2).map((kw, j) => (
                        <span key={j} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{kw}</span>
                      ))}
                    </div>
                    {i === (schedulerStatus.config?.current_topic_index % schedulerStatus.config?.topics_rotation.length) && (
                      <span className="text-blue-600 text-xs font-medium mt-2 block">← Next Up</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Media Outlets Tab */}
        {activeTab === 'outlets' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-800">📡 Media Outlets ({outlets.length})</h3>
              <button onClick={() => setShowOutletModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">+ Add Outlet</button>
            </div>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emails Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {outlets.map(outlet => (
                    <tr key={outlet.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{outlet.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{outlet.email}</td>
                      <td className="px-4 py-3 text-sm capitalize">{outlet.type}</td>
                      <td className="px-4 py-3 text-sm capitalize">{outlet.location?.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm">{outlet.emails_sent || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {outlets.length === 0 && <div className="p-8 text-center text-gray-500">No media outlets added yet.</div>}
            </div>
          </div>
        )}

        {/* Free PR Sites Tab */}
        {activeTab === 'free-sites' && (
          <div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-yellow-800">🆓 Free Press Release Distribution Sites</h3>
              <p className="text-yellow-700 text-sm mt-1">
                <strong>How it works:</strong> These are <u>manual submission</u> sites. Click "Visit →" to open the submission page, 
                then copy/paste your press release content. Most require a free account.
              </p>
              <div className="flex flex-wrap gap-4 mt-3 text-xs">
                <span className="flex items-center gap-1"><span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">free</span> = Completely free submission</span>
                <span className="flex items-center gap-1"><span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">freemium</span> = Free tier with optional paid upgrades</span>
                <span className="flex items-center gap-1"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">pitch</span> = Email editors directly (not automated)</span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DA</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {freeSites.map((site, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{site.name}</p>
                      </td>
                      <td className="px-4 py-3">{getSiteTypeBadge(site.type)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-bold ${site.da >= 70 ? 'text-green-600' : site.da >= 50 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {site.da || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-600">{site.notes}</p>
                      </td>
                      <td className="px-4 py-3">
                        <a href={site.submission_url || site.url} target="_blank" rel="noreferrer" className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm hover:bg-blue-200 inline-block">
                          Visit →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Quick Copy Section */}
            {releases.length > 0 && (
              <div className="mt-6 bg-white rounded-lg shadow p-6">
                <h4 className="font-semibold text-gray-800 mb-2">📋 Quick Copy - Latest Press Release</h4>
                <p className="text-sm text-gray-600 mb-3">Select a press release to copy its content for manual submission:</p>
                <select className="border rounded px-3 py-2 w-full mb-3" onChange={e => {
                  const release = releases.find(r => r.id === e.target.value);
                  if (release) {
                    navigator.clipboard.writeText(`${release.title}\n\n${release.content || release.excerpt}`);
                    alert('Press release copied to clipboard!');
                  }
                }}>
                  <option value="">Select a press release...</option>
                  {releases.filter(r => r.status === 'published').map(r => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">✨ Generate Press Release</h3>
            <form onSubmit={handleGenerate} className="space-y-4">
              <input placeholder="Title" value={generateForm.title} onChange={e => setGenerateForm({...generateForm, title: e.target.value})} className="w-full border rounded px-3 py-2" required />
              <input placeholder="Topic (e.g., Dental Implants, LANAP)" value={generateForm.topic} onChange={e => setGenerateForm({...generateForm, topic: e.target.value})} className="w-full border rounded px-3 py-2" required />
              <input placeholder="Keywords (comma separated)" value={generateForm.keywords} onChange={e => setGenerateForm({...generateForm, keywords: e.target.value})} className="w-full border rounded px-3 py-2" />
              <textarea placeholder="Additional context (optional)" value={generateForm.custom_content} onChange={e => setGenerateForm({...generateForm, custom_content: e.target.value})} rows={3} className="w-full border rounded px-3 py-2" />
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={generateForm.generate_with_ai} onChange={e => setGenerateForm({...generateForm, generate_with_ai: e.target.checked})} />
                <span className="text-sm">Generate with AI</span>
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={generating} className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                  {generating ? 'Generating...' : 'Generate'}
                </button>
                <button type="button" onClick={() => setShowGenerateModal(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Outlet Modal */}
      {showOutletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">📡 Add Media Outlet</h3>
            <form onSubmit={handleAddOutlet} className="space-y-4">
              <input placeholder="Outlet Name" value={outletForm.name} onChange={e => setOutletForm({...outletForm, name: e.target.value})} className="w-full border rounded px-3 py-2" required />
              <input type="email" placeholder="Email" value={outletForm.email} onChange={e => setOutletForm({...outletForm, email: e.target.value})} className="w-full border rounded px-3 py-2" required />
              <select value={outletForm.type} onChange={e => setOutletForm({...outletForm, type: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="newspaper">Newspaper</option>
                <option value="tv">TV Station</option>
                <option value="radio">Radio</option>
                <option value="online">Online Publication</option>
                <option value="press_release_site">Press Release Site</option>
              </select>
              <select value={outletForm.location} onChange={e => setOutletForm({...outletForm, location: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="fort_worth">Fort Worth</option>
                <option value="dallas">Dallas</option>
                <option value="arlington">Arlington</option>
                <option value="dfw">DFW Metro</option>
                <option value="national">National</option>
              </select>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700">Add Outlet</button>
                <button type="button" onClick={() => setShowOutletModal(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && selectedRelease && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">📤 Send: {selectedRelease.title}</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Select outlets to send to:</p>
              <div className="max-h-60 overflow-y-auto border rounded p-2">
                {outlets.map(outlet => (
                  <label key={outlet.id} className="flex items-center gap-2 py-1">
                    <input type="checkbox" checked={sendForm.selectedOutlets.includes(outlet.id)} onChange={() => toggleOutletSelection(outlet.id)} />
                    <span className="text-sm">{outlet.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 mb-4">
              <input type="checkbox" checked={sendForm.include_free_sites} onChange={e => setSendForm({...sendForm, include_free_sites: e.target.checked})} />
              <span className="text-sm">Show free submission sites after sending</span>
            </label>
            <div className="flex gap-2">
              <button onClick={handleSend} disabled={sendForm.selectedOutlets.length === 0} className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                Send to {sendForm.selectedOutlets.length} Outlets
              </button>
              <button onClick={() => { setShowSendModal(false); setSelectedRelease(null); }} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PressReleases;
